import React from 'react';
import { Mail, Phone, MapPin, Linkedin, Instagram } from 'lucide-react';
import { companyInfo, socialMedia } from '../mockData';
import ContactForm from './ContactForm';
import { WhatsAppIcon } from './WhatsAppButton';
import { Reveal } from './Reveal';

const iconMap = { Linkedin, Instagram };

const Contact = ({ showHeader = true }) => {
  return (
    <section id="contact" className="py-24 bg-gray-50 relative">
      <div className="container mx-auto px-6">
        {showHeader && (
          <Reveal className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-charcoal mb-4">
              Get in <span className="text-brand-red">Touch</span>
            </h2>
            <p className="text-xl text-gray-600">
              Ready to start your project? Contact us today for a consultation
            </p>
          </Reveal>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6">
            <Reveal>
              <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-100" data-testid="contact-email-card">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                  <Mail className="w-6 h-6 text-brand-red" />
                </div>
                <h3 className="text-lg font-bold text-charcoal mb-2">Email Us</h3>
                <a href={`mailto:${companyInfo.email.admin}`} className="text-gray-600 hover:text-brand-red text-sm block mb-1 transition-colors">
                  {companyInfo.email.admin}
                </a>
                <a href={`mailto:${companyInfo.email.director}`} className="text-gray-600 hover:text-brand-red text-sm block transition-colors">
                  {companyInfo.email.director}
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-100" data-testid="contact-phone-card">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                  <Phone className="w-6 h-6 text-brand-red" />
                </div>
                <h3 className="text-lg font-bold text-charcoal mb-2">Call Us</h3>
                <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="text-gray-600 hover:text-brand-red text-sm transition-colors">
                  {companyInfo.phone}
                </a>
                <a
                  href={`https://wa.me/${companyInfo.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-link"
                  className="mt-3 inline-flex items-center space-x-2 bg-[#25D366]/10 text-[#128C4A] hover:bg-[#25D366] hover:text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                >
                  <WhatsAppIcon className="w-4 h-4" />
                  <span>Chat on WhatsApp</span>
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-100" data-testid="contact-location-card">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-brand-red" />
                </div>
                <h3 className="text-lg font-bold text-charcoal mb-2">Visit Us</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{companyInfo.address}</p>
              </div>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100" data-testid="contact-social-card">
                <h3 className="text-lg font-bold text-charcoal mb-4">Follow Us</h3>
                <div className="flex space-x-3">
                  {socialMedia.map((social) => {
                    const Icon = iconMap[social.icon];
                    return (
                      <a
                        key={social.name}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={social.name}
                        className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center hover:bg-brand-red group transition-colors"
                      >
                        <Icon className="w-5 h-5 text-brand-red group-hover:text-white transition-colors" />
                      </a>
                    );
                  })}
                </div>
              </div>
            </Reveal>
          </div>

          {/* Contact Form + Map */}
          <div className="lg:col-span-2">
            <Reveal>
              <div className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-8">
                <ContactForm />
              </div>
            </Reveal>
            <Reveal delay={0.15}>
  <div className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
        <MapPin className="w-6 h-6 text-brand-red" />
      </div>

      <div>
        <h3 className="text-xl font-bold text-charcoal mb-2">
          Find Us
        </h3>

        <p className="text-gray-600 text-sm leading-relaxed mb-5">
          {companyInfo.address}
        </p>

        <a
          href="https://www.google.com/maps/search/?api=1&query=273+Petsana,+Nketoana+Township,+Reitz,+Free+State,+South+Africa"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center bg-brand-red text-white px-5 py-3 rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors"
        >
          Get Directions
        </a>
      </div>
    </div>
  </div>
</Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
